/**
 * QR Scanner Component for Delivery Completion
 * Uses html5-qrcode library
 */
import { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from './ui/button';
import { X, Camera, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const QRScanner = ({ isOpen, onClose, onScanSuccess, orderId }) => {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    if (isOpen && !scanning) {
      startScanner();
    }
    
    return () => {
      stopScanner();
    };
  }, [isOpen]);

  const startScanner = async () => {
    try {
      setError(null);
      setScanning(true);
      
      const html5QrCode = new Html5Qrcode('qr-reader');
      html5QrCodeRef.current = html5QrCode;
      
      await html5QrCode.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0
        },
        async (decodedText) => {
          // QR code scanned successfully
          console.log('Scanned:', decodedText);
          await stopScanner();
          
          // Verify QR code matches order
          if (decodedText.includes(orderId) || decodedText.includes('LNT-')) {
            toast.success('QR Code verified!');
            onScanSuccess(decodedText);
          } else {
            toast.error('Invalid QR code for this delivery');
            setError('QR code does not match this order');
            setScanning(false);
          }
        },
        (errorMessage) => {
          // Ignore scan errors (continuous scanning)
        }
      );
    } catch (err) {
      console.error('Scanner error:', err);
      setError('Failed to access camera. Please allow camera permissions.');
      setScanning(false);
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current = null;
      } catch (e) {
        console.log('Scanner already stopped');
      }
    }
    setScanning(false);
  };

  const handleClose = async () => {
    await stopScanner();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-black/50">
        <h2 className="text-white font-bold flex items-center gap-2">
          <Camera className="h-5 w-5 text-emerald-400" />
          Scan Customer QR Code
        </h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClose}
          className="text-white hover:bg-white/20"
        >
          <X className="h-6 w-6" />
        </Button>
      </div>

      {/* Scanner Area */}
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        <div 
          id="qr-reader" 
          ref={scannerRef}
          className="w-full max-w-sm rounded-2xl overflow-hidden bg-black"
          style={{ minHeight: '300px' }}
        />
        
        {/* Instructions */}
        <div className="mt-6 text-center">
          <p className="text-white text-sm mb-2">
            Point camera at the QR code on customer's phone
          </p>
          <p className="text-gray-400 text-xs">
            Order: #{orderId?.slice(0, 8)}
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mt-4 bg-red-500/20 border border-red-500/50 rounded-lg p-3 max-w-sm">
            <p className="text-red-300 text-sm text-center">{error}</p>
            <Button
              onClick={startScanner}
              className="w-full mt-2 bg-red-600 hover:bg-red-700"
              size="sm"
            >
              Try Again
            </Button>
          </div>
        )}

        {/* Scanning Indicator */}
        {scanning && !error && (
          <div className="mt-4 flex items-center gap-2 text-emerald-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Scanning...</span>
          </div>
        )}
      </div>

      {/* Cancel Button */}
      <div className="p-4 bg-black/50">
        <Button
          onClick={handleClose}
          variant="outline"
          className="w-full border-white/30 text-white hover:bg-white/10"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default QRScanner;
